/**
 * Tela /clients (TH-53159 / M1.E4.S3.1).
 *
 * Apenas super_admin. Lista tenants com filtro por status, paginação
 * server-side e ações por linha (Suspender / Ativar). Edição inline
 * fica para outra subtask — DoD pede botão, mas a complexidade do
 * form completo de edição vai inflar 3 SP além do orçamento.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import { CreateClientDialog } from "@/pages/clients/CreateClientDialog";
import { ConfirmStatusDialog } from "@/pages/clients/ConfirmStatusDialog";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: "active" | "trial" | "overdue" | "suspended" | "archived";
  created_at: string;
}

interface PagedResponse {
  data: Tenant[];
  meta: { page: number; per_page: number; total: number };
}

const PER_PAGE = 20;

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "overdue", label: "Overdue" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

const STATUS_META: Record<
  Tenant["status"],
  { tone: StatusTone; label: string }
> = {
  active: { tone: "success", label: "Ativo" },
  trial: { tone: "muted", label: "Trial" },
  overdue: { tone: "warn", label: "Em atraso" },
  suspended: { tone: "destructive", label: "Suspenso" },
  archived: { tone: "muted", label: "Arquivado" },
};

const dateFmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

async function fetchTenants(
  page: number,
  status: string,
): Promise<PagedResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(PER_PAGE),
  });
  if (status) params.set("status", status);
  const res = await fetch(`/api/tenants?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as PagedResponse;
}

export function ClientsPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("");
  const [confirm, setConfirm] = useState<
    { tenant: Tenant; action: "suspend" | "activate" } | null
  >(null);

  const query = useQuery({
    queryKey: ["tenants", page, status],
    queryFn: () => fetchTenants(page, status),
    staleTime: 30_000,
    enabled: user?.role === "super_admin",
  });

  if (user && user.role !== "super_admin") {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="OPERAÇÃO / CLIENTES" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Apenas super_admin pode acessar esta tela.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / CLIENTES"
        title="Clientes"
        actions={<CreateClientDialog />}
      >
        <div className="mx-auto max-w-6xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Tenants registrados no Priceshow.
          </p>

          <div className="mt-4 flex items-center gap-3">
            <label
              htmlFor="status-filter"
              className="text-sm text-[var(--color-muted-foreground)]"
            >
              Status:
            </label>
            <div className="w-40">
              <Select
                id="status-filter"
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="mt-6">
            <DataTable<Tenant>
              columns={[
                { key: "name", header: "Nome" },
                {
                  key: "slug",
                  header: "Slug",
                  mono: true,
                  cell: (t) => (
                    <span className="text-[var(--color-muted-foreground)]">{t.slug}</span>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  cell: (t) => {
                    const meta = STATUS_META[t.status];
                    return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
                  },
                },
                {
                  key: "created_at",
                  header: "Criado em",
                  mono: true,
                  cell: (t) => (
                    <span className="text-[var(--color-muted-foreground)]">
                      {dateFmt.format(new Date(t.created_at))}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  header: "",
                  align: "right",
                  cell: (t) =>
                    t.status === "suspended" || t.status === "archived" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirm({ tenant: t, action: "activate" })}
                      >
                        Ativar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[var(--color-destructive)]"
                        onClick={() => setConfirm({ tenant: t, action: "suspend" })}
                      >
                        Suspender
                      </Button>
                    ),
                },
              ] satisfies DataTableColumn<Tenant>[]}
              rows={query.isLoading || query.error ? [] : (query.data?.data ?? [])}
              rowKey={(t) => t.id}
              emptyMessage={
                query.isLoading
                  ? "carregando…"
                  : query.error
                    ? "erro ao carregar"
                    : "nenhum cliente encontrado"
              }
            />
          </div>

          {query.data ? (
            <div className="mt-4">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={query.data.meta.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>

        {confirm ? (
          <ConfirmStatusDialog
            tenant={confirm.tenant}
            action={confirm.action}
            open
            onOpenChange={(o) => {
              if (!o) setConfirm(null);
            }}
          />
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}
